// PARTNERS > IMPORT PARTNER DOCUMENTATION (owner, 2026-10-15: "add import
// option in partners like doc control, and like techncial documentation,
// where we can click import partners documentation, which will read many
// pdfs and extract partners ... then show a UI to validate edit or map
// exitisting partners to newly imported partners in the review center").
//
//   <PartnerImportButton projectId onUploaded />   the button + upload dialog
//   <PartnerImportStatus projectId reloadKey canRetry />  what each import is
//                                                  doing, polled while one runs
//
// The button is drawn for whoever holds participantAdd (the server's gate for
// the upload). The files go into Document Control first, then a job on the
// heavy lane reads them; the review itself is the Review Center's "Partner
// import" tab, which this links to. Nothing here adds anyone to a roster.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Progress, Space, Tag, Upload, message } from "antd";
import { FileSearchOutlined, InboxOutlined, ReloadOutlined } from "@ant-design/icons";
import { uploadPartnerDocs, listPartnerImports, retryPartnerImport } from "api/participants/partnerImportApi";

const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.xls,.csv,.tsv,.txt";
const MAX_FILES = 40;

export function PartnerImportButton({ projectId, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!files.length) return message.warning("Choose the documents to read first");
    setBusy(true);
    try {
      const r = await uploadPartnerDocs(projectId, files);
      const n = files.length;
      message.success(`${n} document${n === 1 ? "" : "s"} filed in Document Control and queued for reading - the proposed partners will wait in the Review Center`);
      if (r.rejected && r.rejected.length) message.warning(`Not read (unsupported type): ${r.rejected.join(", ")}`);
      setFiles([]);
      setOpen(false);
      if (onUploaded) onUploaded(r.import);
    } catch (e) {
      message.error(e.message || "Could not start the import");
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <>
      <Button icon={<FileSearchOutlined />} onClick={() => setOpen(true)} data-testid="partner-import-open">
        Import partner documentation
      </Button>
      <Modal
        open={open}
        title="Import partner documentation"
        okText={busy ? "Uploading..." : files.length ? `Import ${files.length} document${files.length === 1 ? "" : "s"}` : "Import"}
        okButtonProps={{ disabled: !files.length, loading: busy, "data-testid": "partner-import-submit" }}
        onOk={submit}
        onCancel={() => { if (!busy) setOpen(false); }}
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: "#667085", fontSize: 13 }}>
          Rosters, attendee lists, letterheads, contact sheets - the documents are filed in
          Document Control as partner documentation, then read for partner companies, their
          trade, their people and email addresses. Nothing is added to the roster and nobody
          is emailed until you review and apply the result in the Review Center.
        </p>
        <Upload.Dragger
          multiple
          accept={ACCEPT}
          fileList={files.map((f, i) => ({ uid: f.uid || `${i}-${f.name}`, name: f.name, status: "done" }))}
          beforeUpload={(f, list) => {
            setFiles((prev) => [...prev, ...list.filter((x) => !prev.some((p) => p.uid === x.uid))].slice(0, MAX_FILES));
            return false;
          }}
          onRemove={(f) => { setFiles((prev) => prev.filter((p) => (p.uid || p.name) !== f.uid && p.name !== f.name)); }}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Drop the partner documents here, or click to choose</p>
          <p className="ant-upload-hint">PDF first of all; Word (.docx), Excel and CSV contact lists too. Up to {MAX_FILES} files.</p>
        </Upload.Dragger>
      </Modal>
    </>
  );
}

const reviewHref = (projectId) => `/approval-queue?projectId=${projectId}&tab=partnerImports`;

function statusLine(imp, projectId) {
  const p = imp.progress || {};
  switch (imp.status) {
    case "queued": return <Tag>Queued - waiting for the worker</Tag>;
    case "reading": return (
      <Space size={6}>
        <Tag color="processing">{`Reading ${Math.min((p.done || 0) + 1, p.total || 1)} of ${p.total || 1}`}</Tag>
        <Progress percent={Math.round(((p.done || 0) / Math.max(1, p.total || 1)) * 100)} size="small" style={{ width: 120, margin: 0 }} showInfo={false} />
        <span style={{ fontSize: 12, color: "#667085" }}>{p.stage}</span>
      </Space>
    );
    case "ready": return (
      <Space size={6}>
        <Tag color="gold">Ready for review</Tag>
        <span style={{ fontSize: 12 }}>{imp.partners} partner{imp.partners === 1 ? "" : "s"}, {imp.people} people</span>
        <a href={reviewHref(projectId)}>Review in the Review Center</a>
      </Space>
    );
    case "applying": return <Tag color="processing">Applying...</Tag>;
    case "applied": return <Tag color="green">{`Applied${imp.applied && imp.applied.byName ? ` by ${imp.applied.byName}` : ""}`}</Tag>;
    case "failed": return <Tag color="red">Failed</Tag>;
    default: return <Tag>{imp.status}</Tag>;
  }
}

export function PartnerImportStatus({ projectId, reloadKey, canRetry }) {
  const [rows, setRows] = useState([]);
  const [hidden, setHidden] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await listPartnerImports(projectId);
      setRows((r.imports || []).slice(0, 5));
      setHidden(false);
    } catch (e) {
      // Without participantView + reviewCenterView the list is not theirs to see.
      if (e.status === 403) setHidden(true);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load, reloadKey]);
  // Poll while something is queued or being read; stop when nothing is.
  const running = rows.some((r) => r.status === "queued" || r.status === "reading" || r.status === "applying");
  useEffect(() => {
    if (!running) return undefined;
    timer.current = setInterval(load, 4000);
    return () => clearInterval(timer.current);
  }, [running, load]);

  if (hidden || !rows.length) return null;
  return (
    <div data-testid="partner-import-status" style={{ border: "1px solid #EFEFEF", borderRadius: 8, padding: "10px 12px", margin: "0 0 14px" }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Partner documentation imports</div>
      {rows.map((imp) => (
        <div key={imp._id} style={{ padding: "6px 0", borderTop: "1px solid #F5F5F5" }}>
          <Space wrap size={8}>
            <span style={{ fontSize: 12, color: "#667085" }}>
              {new Date(imp.createdAt).toLocaleString()} · {(imp.files || []).length} file{(imp.files || []).length === 1 ? "" : "s"}
              {imp.createdByName ? ` · ${imp.createdByName}` : ""}
            </span>
            {statusLine(imp, projectId)}
            {imp.status === "failed" && canRetry && (
              <Button size="small" icon={<ReloadOutlined />} onClick={async () => {
                try { await retryPartnerImport(projectId, imp._id); load(); } catch (e) { message.error(e.message); }
              }}>Read again</Button>
            )}
          </Space>
          {imp.status === "failed" && imp.error && <div style={{ color: "#b42318", fontSize: 12, marginTop: 4 }}>{imp.error}</div>}
          {imp.status === "ready" && imp.extraction && imp.extraction.note && (
            <Alert type="warning" showIcon style={{ marginTop: 6, fontSize: 12 }} message={imp.extraction.note} />
          )}
          {(imp.files || []).filter((f) => f.note || f.docControlError).map((f) => (
            <div key={f.name} style={{ fontSize: 12, color: "#667085", marginTop: 2 }}>
              {f.name}: {f.note || `not filed in Document Control (${f.docControlError})`}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default PartnerImportButton;
